import { RichText, RichTextValue } from "@/components/rich-text"
import { SlideOnView } from "@/components/slide-on-view"
import { client } from "@/root/contents/sanity/lib/client"
import { GridBg } from "@/svgs/grid-bg"
import { cn } from "@/utils/cn"
import { useInView } from "motion/react"
import { useNextSanityImage } from "next-sanity-image"
import Image from "next/image"
import { ComponentRef, useRef } from "react"
import { SanityImageAsset, SanityReference } from "sanity-codegen"

interface OurOfficesProps {
  content: {
    variant: "white" | "blue"
    title: RichTextValue
    description: string
    offices: {
      location: string
      image: {
        asset: SanityReference<SanityImageAsset>
      }
    }[]
  }
}

export function OurOffices({ content }: OurOfficesProps) {
  const containerRef = useRef<ComponentRef<"div">>(null)
  const inView = useInView(containerRef, {
    margin: "0px 0px -100% 0px",
    amount: 0,
  })

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative h-auto",
        content.variant === "white"
          ? "bg-neutral-light text-neutral-dark"
          : "bg-primary-800 text-neutral-light"
      )}
    >
      {content.variant === "white" && (
        <GridBg
          className={cn(
            inView ? "fixed md:absolute" : "absolute",
            "inset-0 z-0 h-full w-full"
          )}
        />
      )}
      <div
        className={cn("relative py-15 md:px-4", "md:mx-16 lg:mx-20 xl:mx-35")}
      >
        <div
          className={cn(
            "relative z-10 max-w-[63rem] px-4 md:px-0 lg:max-w-[90rem]"
          )}
        >
          <SlideOnView>
            <RichText value={content.title} />
          </SlideOnView>
          <SlideOnView delay={0.3} className="mt-6 text-lg xl:mt-8 xl:text-xlg">
            {content.description}
          </SlideOnView>
        </div>
        <div className="mt-12 flex max-w-[63rem] flex-col items-center justify-center overflow-hidden md:max-w-[166rem]">
          {content.offices.map((office, index) => (
            <Office
              key={index}
              office={office}
              variant={content.variant}
              isOdd={index % 2 === 0}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function Office({
  office,
  variant,
  isOdd,
}: {
  office: OurOfficesProps["content"]["offices"][number]
  variant: OurOfficesProps["content"]["variant"]
  isOdd: boolean
}) {
  const imageProps = useNextSanityImage(client, office.image.asset)

  return (
    <div
      className={cn(
        "group relative h-[10rem] w-full overflow-hidden transition-colors duration-300 md:h-[16rem]",
        variant === "white"
          ? "md:text-neutral-dark md:hover:text-neutral-light"
          : "text-neutral-light",
        variant === "white" &&
          (isOdd ? "text-neutral-light" : "text-neutral-dark")
      )}
    >
      <h2
        className={cn(
          "absolute top-1/2 z-10 w-max -translate-y-1/2 px-4 text-7xl font-bold text-current transition-[left,transform,color] duration-300 md:left-0 md:translate-x-0 md:px-0 md:text-10xl md:group-hover:left-full md:group-hover:-translate-x-full",
          isOdd ? "left-full -translate-x-full" : "left-0 translate-x-0"
        )}
      >
        {office.location}
      </h2>
      <div
        className={cn(
          "-z-10 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100",
          isOdd ? "opacity-100" : "opacity-0"
        )}
      >
        <Image fill src={imageProps.src} alt="" className="object-cover" />
      </div>
    </div>
  )
}
